import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { usePrevious } from 'hooks';
import { Autocomplete } from 'components/FormFields';
import { userGroupsActions } from 'state/modules/userGroups';
import { debounce } from 'lodash';
import { truncateKeywordSpaces } from 'utils/utils';

const UserGroupsSelect = ({
  label,
  variant,
  field: { name, value },
  customParams,
  form: { errors, touched, setFieldValue },
  disabled,
  omitFields,
  omitFieldKey,
  margin,
  required,
  onChange,
  dataCy,
}) => {
  const dispatch = useDispatch();
  const [defaultValue, setDefaultValue] = useState(null);
  const [params, setParams] = useState({
    page: 1,
    limit: 20,
    ...customParams,
  });
  const prevvalue = usePrevious(value);

  const { userGroups, userGroupsLoading } = useSelector((state) => state.userGroups);

  const fetchUserGroups = useCallback(
    debounce((params) => {
      const newParams = { ...params };
      setParams(newParams);
      if (newParams?.keyword) {
        newParams.keyword = truncateKeywordSpaces(newParams.keyword);
      }
      dispatch(userGroupsActions.fetchUserGroups(newParams));
    }, 1000),
    []
  );

  useEffect(() => {
    fetchUserGroups(params);
  }, []) // eslint-disable-line

  useEffect(() => {
    if (prevvalue !== value || !defaultValue) {
      const defaultVal = userGroups.find((el) => el.id === parseInt(value));
      if (defaultVal) {
        setDefaultValue(defaultVal);
      } else {
        setDefaultValue(null);
      }
    }
  }, [value, userGroups]) // eslint-disable-line

  const handleFetchUserGroups = () => {
    if (userGroups.length === 0 && !userGroupsLoading && !params?.keyword)
      dispatch(userGroupsActions.fetchUserGroups(params));
  };

  return (
    <Autocomplete
      label={label}
      margin={margin}
      fullwidth="true"
      required={required}
      variant={variant}
      disabled={disabled}
      options={userGroups.filter((value) => omitFields.indexOf(value[omitFieldKey]) === -1)}
      onHighlightChange={handleFetchUserGroups}
      value={defaultValue}
      clearOnBlur={false}
      inputProps={{'data-cy': 'user-groups-select'}}
      id="user-groups-select"
      dataCy={dataCy}
      loading={userGroupsLoading}
      getOptionLabel={(option) => option.name || ''}
      onInputChange={(ev, val) => {
        fetchUserGroups({ ...params, keyword: val });
      }}
      onChange={(ev, val) => {
        setFieldValue(name, val?.id || '');
        if (onChange) {
          onChange(name, val);
        }
      }}
      error={errors && errors[name] && touched && touched[name]}
      getOptionSelected={(option, value) => option.id === value.id}
      helperText={errors && errors[name] && touched && touched[name] && errors[name]}
    />
  );
};

UserGroupsSelect.propTypes = {
  label: PropTypes.string,
  margin: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  field: PropTypes.shape({
    value: PropTypes.any,
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
  }),
  form: PropTypes.shape({
    errors: PropTypes.object,
    touched: PropTypes.object,
  }),
  customParams: PropTypes.object,
  omitFields: PropTypes.array,
  omitFieldKey: PropTypes.string,
  variant: PropTypes.oneOf(['standard', 'outlined', 'filled']),
  dataCy: PropTypes.string,
};

UserGroupsSelect.defaultProps = {
  required: false,
  disabled: false,
  margin: 'normal',
  form: {
    errors: {},
    touched: {},
  },
  omitFields: [],
  customParams: {},
  variant: 'standard',
  label: 'Select...',
  dataCy: 'user_groups-select',
};

export default UserGroupsSelect;
